import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../api/client";
import { useParams2 } from "../contexts/ParamsContext";

const PUB = process.env.PUBLIC_URL || "";

function fmtFR(iso) {
  if (!iso) return "_____________";
  return new Date(iso + "T00:00:00").toLocaleDateString("fr-FR",
    { day: "2-digit", month: "2-digit", year: "numeric" });
}

// ====== STRUCTURE DE L'ÉTAT DES LIEUX ======
const SECTIONS = [
  {
    titre: "1. Salle principale",
    items: [
      "État des murs (taches, traces, impacts)",
      "État du sol (carrelage, joints, propreté)",
      "État du plafond et luminaires",
      "Fenêtres et volets (fermeture, vitres)",
      "Porte d'entrée principale (fonctionnement, serrure)",
      "Système de chauffage / climatisation",
      "Prises électriques et interrupteurs",
      "Fils en acier latéraux pour décorations",
      "Vidéoprojecteur (présence, fonctionnement)",
      "Barre de son et installation audio",
      "Connexion Wi-Fi / Internet (test)",
    ],
  },
  {
    titre: "2. Cuisine",
    items: [
      "Réfrigérateur(s) — fonctionnement, propreté intérieure",
      "Congélateur — fonctionnement, propreté",
      "Cuisinière 5 feux (tous les feux testés)",
      "Grand four (fonctionnement, propreté intérieure)",
      "Lave-vaisselle (fonctionnement, filtres)",
      "Évier et robinetterie (écoulement, fuites)",
      "Plan de travail (état, propreté)",
      "Hotte aspirante / VMC",
      "Placards et rangements",
    ],
  },
  {
    titre: "3. Sanitaires",
    items: [
      "WC standard — propreté et fonctionnement (chasse, abattant)",
      "WC PMR — propreté, fonctionnement, accessoires",
      "Lavabos (écoulement, robinetterie)",
      "Distributeur de savon / papier",
      "Sèche-mains ou essuie-tout",
      "Miroirs et accessoires",
    ],
  },
  {
    titre: "4. Sécurité (à vérifier impérativement)",
    items: [
      "Extincteur(s) — présence, plombage intact",
      "Blocs autonomes d'éclairage de secours (BAES) — voyants allumés",
      "Plan d'évacuation affiché et visible",
      "Issues de secours dégagées et déverrouillables",
      "Alarme incendie (si présente) — état",
      "Boîtier électrique accessible",
      "Trousse de premiers secours — présence",
    ],
  },
  {
    titre: "5. Abords extérieurs",
    items: [
      "Parking et stationnement (état général)",
      "Accès PMR (rampe, cheminement)",
      "Éclairage extérieur",
      "Espaces verts et propreté",
      "Containers / poubelles (présence, propreté)",
    ],
  },
];

// Tables et chaises - inventaire à compter
const INVENTAIRE = [
  { label: "Nombre de tables", initial: "" },
  { label: "Nombre de chaises", initial: "" },
  { label: "Nombre de bancs (le cas échéant)", initial: "" },
];

const COMPTEURS = [
  { label: "Compteur électricité (index)", unite: "kWh" },
  { label: "Compteur eau (index)", unite: "m³" },
];

const ETATS = ["Bon état", "État moyen", "Mauvais état", "Manquant", "N/A"];

export default function EtatDesLieux() {
  const { id } = useParams();
  const params = useParams2();
  const [resa, setResa] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/salle/reservations/${id}`)
      .then(setResa)
      .catch(() => setError("Réservation introuvable. Vérifiez le lien."));
  }, [id]);

  if (error) {
    return <div className="container"><div className="error">{error}</div></div>;
  }
  if (!resa) {
    return <div className="container"><p className="muted">Chargement…</p></div>;
  }

  return (
    <div className="container">
      <div className="contrat-toolbar no-print">
        <Link to={`/contrat-salle/${id}`} className="secondary"
              style={{ padding: "0.5rem 1rem", border: "1px solid rgba(0,0,0,0.15)",
                       borderRadius: 6, textDecoration: "none" }}>
          ← Retour au contrat
        </Link>
        <h2 style={{ margin: 0 }}>État des lieux d'entrée et de sortie</h2>
        <button onClick={() => window.print()}>
          🖨 Imprimer / Sauvegarder en PDF
        </button>
      </div>

      <div className="contrat-notice no-print">
        <strong>📋 Procédure d'utilisation</strong>
        <ol style={{ marginBottom: 0 }}>
          <li>Imprimez ce document <strong>en deux exemplaires</strong> (mairie + locataire)</li>
          <li>À l'entrée : remplir la colonne <strong>« Entrée »</strong> contradictoirement
              avec un représentant de la mairie</li>
          <li>À la sortie : remplir la colonne <strong>« Sortie »</strong> contradictoirement</li>
          <li>Signer les deux exemplaires aux deux états des lieux</li>
        </ol>
      </div>

      {/* ============== DOCUMENT IMPRIMABLE ============== */}
      <article className="contrat-doc edl-doc">
        {/* En-tête */}
        <header className="contrat-header">
          <img src={`${PUB}/logo-lcsf.png`} alt="Blason" />
          <div>
            <strong>Département de la Sarthe</strong><br/>
            <strong>{params.mairie_nom || "Mairie de La Chapelle-Saint-Fray"}</strong><br/>
            {params.mairie_adresse || "Route du Mans"}<br/>
            {(params.mairie_cp || "72240") + " " + (params.mairie_ville || "La Chapelle-Saint-Fray")}<br/>
            Tél : {params.mairie_telephone || "02 43 20 90 38"}
          </div>
        </header>

        <h1 className="contrat-title">État des lieux contradictoire<br/>de la salle communale</h1>

        <p className="contrat-ref muted">
          Référence location : <strong>n° {resa.id}</strong> du {fmtFR(resa.date_debut)}
          {resa.date_fin !== resa.date_debut && <> au {fmtFR(resa.date_fin)}</>}
        </p>

        {/* ENTÊTE LOCATAIRE */}
        <section className="edl-header-info">
          <table className="contrat-table">
            <tbody>
              <tr><td>Locataire :</td><td><strong>{resa.nom?.toUpperCase()} {resa.prenom}</strong></td></tr>
              <tr><td>Email / téléphone :</td>
                  <td>{resa.email} {resa.telephone && <>· {resa.telephone}</>}</td></tr>
              <tr><td>Événement :</td>
                  <td>{resa.type_evenement || "…………………………………………………………………………"}</td></tr>
            </tbody>
          </table>
        </section>

        {/* DATES DES ÉTATS DES LIEUX */}
        <section className="edl-dates">
          <table className="edl-dates-table">
            <thead>
              <tr>
                <th></th>
                <th>État des lieux d'entrée</th>
                <th>État des lieux de sortie</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <th>Date</th>
                <td>{fmtFR(resa.date_debut)} <small className="muted">prévu</small></td>
                <td>{fmtFR(resa.date_fin)} <small className="muted">prévu</small></td>
              </tr>
              <tr>
                <th>Heure</th>
                <td>{resa.heure_debut || "14:00"}</td>
                <td>{resa.heure_fin || "18:00"}</td>
              </tr>
              <tr>
                <th>Représentant de la mairie</th>
                <td className="fillable">………………………………………………</td>
                <td className="fillable">………………………………………………</td>
              </tr>
            </tbody>
          </table>
        </section>

        {/* CLÉS */}
        <section>
          <h3>Clés remises et restituées</h3>
          <table className="edl-keys">
            <thead>
              <tr>
                <th>Type de clé</th>
                <th>Nombre remis à l'entrée</th>
                <th>Nombre restitué à la sortie</th>
              </tr>
            </thead>
            <tbody>
              <tr><td>Clé porte principale</td>
                  <td className="fillable">………</td><td className="fillable">………</td></tr>
              <tr><td>Clé cuisine</td>
                  <td className="fillable">………</td><td className="fillable">………</td></tr>
              <tr><td>Badge / télécommande</td>
                  <td className="fillable">………</td><td className="fillable">………</td></tr>
              <tr><td>Autre : …………………………</td>
                  <td className="fillable">………</td><td className="fillable">………</td></tr>
            </tbody>
          </table>
        </section>

        {/* COMPTEURS */}
        <section>
          <h3>Relevé des compteurs</h3>
          <table className="edl-keys">
            <thead>
              <tr>
                <th>Compteur</th>
                <th>Relevé à l'entrée</th>
                <th>Relevé à la sortie</th>
                <th>Consommation</th>
              </tr>
            </thead>
            <tbody>
              {COMPTEURS.map((c) => (
                <tr key={c.label}>
                  <td>{c.label}</td>
                  <td className="fillable">…………… {c.unite}</td>
                  <td className="fillable">…………… {c.unite}</td>
                  <td className="fillable">…………… {c.unite}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        {/* INVENTAIRE TABLES/CHAISES */}
        <section>
          <h3>Inventaire mobilier</h3>
          <table className="edl-keys">
            <thead>
              <tr>
                <th>Élément</th>
                <th>Quantité à l'entrée</th>
                <th>Quantité à la sortie</th>
                <th>Observations</th>
              </tr>
            </thead>
            <tbody>
              {INVENTAIRE.map((it) => (
                <tr key={it.label}>
                  <td>{it.label}</td>
                  <td className="fillable">………</td>
                  <td className="fillable">………</td>
                  <td className="fillable">………………………………</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="muted edl-mini">
            Les tables et chaises doivent être restituées à l'emplacement initial,
            propres et empilées dans leur zone de rangement.
          </p>
        </section>

        {/* CHECKLIST PAR ZONE */}
        <section className="edl-checklist-section">
          <h3>Vérification par zone</h3>
          <p className="muted edl-mini">
            Pour chaque élément, cocher l'état constaté à l'entrée et à la sortie.
            Toute anomalie doit être détaillée dans la colonne « Observations ».
          </p>

          {SECTIONS.map((sec) => (
            <div key={sec.titre} className="edl-zone">
              <h4>{sec.titre}</h4>
              <table className="edl-checklist">
                <thead>
                  <tr>
                    <th rowSpan="2" style={{ width: "38%" }}>Élément vérifié</th>
                    <th colSpan="2">À l'entrée</th>
                    <th colSpan="2">À la sortie</th>
                  </tr>
                  <tr>
                    <th>État</th>
                    <th>Observation</th>
                    <th>État</th>
                    <th>Observation</th>
                  </tr>
                </thead>
                <tbody>
                  {sec.items.map((item) => (
                    <tr key={item}>
                      <td>{item}</td>
                      <td className="fillable" style={{ minWidth: 60 }}>☐ B ☐ M<br/>☐ ✗ ☐ N/A</td>
                      <td className="fillable">…………………………</td>
                      <td className="fillable" style={{ minWidth: 60 }}>☐ B ☐ M<br/>☐ ✗ ☐ N/A</td>
                      <td className="fillable">…………………………</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}

          <p className="muted edl-mini">
            <strong>Légende des états :</strong> B = Bon état · M = État moyen
            · ✗ = Mauvais état ou manquant · N/A = Non applicable
          </p>
        </section>

        {/* PROPRETÉ GÉNÉRALE */}
        <section>
          <h3>Propreté générale à la sortie</h3>
          <table className="edl-checklist">
            <thead>
              <tr>
                <th>Zone</th>
                <th>Propreté satisfaisante</th>
                <th>Observations</th>
              </tr>
            </thead>
            <tbody>
              {[
                "Salle principale (sol, tables, chaises)",
                "Cuisine (plans, appareils, sol)",
                "Sanitaires",
                "Vaisselle et équipements utilisés",
                "Containers/poubelles (sortis et vidés)",
                "Abords et parking",
              ].map((zone) => (
                <tr key={zone}>
                  <td>{zone}</td>
                  <td className="fillable" style={{ textAlign: "center" }}>☐ Oui ☐ Non</td>
                  <td className="fillable">………………………………………………………</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="muted edl-mini">
            En cas de propreté insuffisante, un forfait ménage pourra être prélevé sur le
            chèque de dépôt de garantie ménage (400&nbsp;€), conformément à l'article 5 du contrat.
          </p>
        </section>

        {/* OBSERVATIONS GÉNÉRALES */}
        <section className="edl-observations">
          <h3>Observations générales — État des lieux d'entrée</h3>
          <div className="edl-obs-box">
            <p className="fillable">
              ………………………………………………………………………………………………………………………………………………………………………………<br/>
              ………………………………………………………………………………………………………………………………………………………………………………<br/>
              ………………………………………………………………………………………………………………………………………………………………………………<br/>
              ………………………………………………………………………………………………………………………………………………………………………………
            </p>
          </div>

          <h3>Observations générales — État des lieux de sortie</h3>
          <div className="edl-obs-box">
            <p className="fillable">
              ………………………………………………………………………………………………………………………………………………………………………………<br/>
              ………………………………………………………………………………………………………………………………………………………………………………<br/>
              ………………………………………………………………………………………………………………………………………………………………………………<br/>
              ………………………………………………………………………………………………………………………………………………………………………………
            </p>
          </div>

          <h3>Dommages constatés et estimation</h3>
          <table className="edl-checklist">
            <thead>
              <tr>
                <th>Description du dommage</th>
                <th>Estimation (€)</th>
              </tr>
            </thead>
            <tbody>
              {[1, 2, 3].map((i) => (
                <tr key={i}>
                  <td className="fillable" style={{ height: 28 }}>…………………………………………………………………………</td>
                  <td className="fillable" style={{ width: 100 }}>……………</td>
                </tr>
              ))}
              <tr style={{ background: "rgba(0,0,0,0.04)" }}>
                <td style={{ textAlign: "right", fontWeight: 600 }}>Total :</td>
                <td className="fillable" style={{ fontWeight: 600 }}>……………</td>
              </tr>
            </tbody>
          </table>
        </section>

        {/* DÉCISION FINALE */}
        <section>
          <h3>Décision finale (à la sortie)</h3>
          <p>
            ☐ <strong>Restitution intégrale</strong> des chèques de dépôt de garantie
            (1 500 € salle + 400 € ménage)<br/>
            ☐ <strong>Encaissement du chèque ménage</strong> de 400 € pour propreté insuffisante<br/>
            ☐ <strong>Encaissement total ou partiel du chèque salle</strong> de 1 500 € pour
            dommages constatés (montant : <span className="fillable">…………… €</span>)<br/>
            ☐ <strong>Facturation complémentaire</strong> pour dommages excédant les dépôts
            (montant : <span className="fillable">…………… €</span>)
          </p>
        </section>

        {/* SIGNATURES */}
        <section className="contrat-signatures">
          <h3>Signatures contradictoires</h3>

          <div style={{ marginBottom: "1.5rem" }}>
            <p style={{ marginBottom: "0.3rem" }}><strong>État des lieux d'entrée</strong> –
              Fait à La Chapelle-Saint-Fray, le <span className="fillable">……../………/…………</span>
            </p>
            <div className="contrat-signature-row">
              <div className="contrat-signature-box">
                <strong>Pour la mairie</strong><br/>
                Nom : <span className="fillable">………………………………</span><br/>
                <em>Mention « Lu et approuvé »</em>
              </div>
              <div className="contrat-signature-box">
                <strong>Le locataire</strong><br/>
                {resa.prenom} {resa.nom}<br/>
                <em>Mention « Lu et approuvé »</em>
              </div>
            </div>
          </div>

          <div>
            <p style={{ marginBottom: "0.3rem" }}><strong>État des lieux de sortie</strong> –
              Fait à La Chapelle-Saint-Fray, le <span className="fillable">……../………/…………</span>
            </p>
            <div className="contrat-signature-row">
              <div className="contrat-signature-box">
                <strong>Pour la mairie</strong><br/>
                Nom : <span className="fillable">………………………………</span><br/>
                <em>Mention « Lu et approuvé »</em>
              </div>
              <div className="contrat-signature-box">
                <strong>Le locataire</strong><br/>
                {resa.prenom} {resa.nom}<br/>
                <em>Mention « Lu et approuvé »</em>
              </div>
            </div>
          </div>
        </section>

        <footer className="contrat-footer muted">
          Document à conserver. Établi en deux exemplaires (mairie + locataire).
          Réf. réservation n° {resa.id} – {params.mairie_nom || "Mairie de La Chapelle-Saint-Fray"}
        </footer>
      </article>
    </div>
  );
}
